import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Trash2, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Note } from "@/types/note";

export interface SimpleNoteTableProps {
  notes: Note[];
  onToggleComplete?: (noteId: string, completed: boolean) => void;
  onDelete?: (noteId: string) => void;
  renderActions?: (note: Note) => React.ReactNode;
  emptyMessage?: string;
  isDeleting?: Set<string>;
  isTogglingComplete?: Set<string>;
}

export function SimpleNoteTable({
  notes,
  onToggleComplete,
  onDelete,
  renderActions,
  emptyMessage = "No notes",
  isDeleting = new Set(),
  isTogglingComplete = new Set(),
}: SimpleNoteTableProps) {
  if (notes.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader className="sr-only">
        <TableRow>
          <TableHead className="w-10">Complete</TableHead>
          <TableHead>Title</TableHead>
          <TableHead className="w-20">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <AnimatePresence mode="popLayout">
          {notes.map((note) => (
            <SimpleNoteRow
              key={note.id}
              note={note}
              onToggleComplete={onToggleComplete}
              onDelete={onDelete}
              renderActions={renderActions}
              isDeleting={isDeleting.has(note.id)}
              isTogglingComplete={isTogglingComplete.has(note.id)}
            />
          ))}
        </AnimatePresence>
      </TableBody>
    </Table>
  );
}

interface SimpleNoteRowProps {
  note: Note;
  onToggleComplete?: (noteId: string, completed: boolean) => void;
  onDelete?: (noteId: string) => void;
  renderActions?: (note: Note) => React.ReactNode;
  isDeleting?: boolean;
  isTogglingComplete?: boolean;
}

function SimpleNoteRow({
  note,
  onToggleComplete,
  onDelete,
  renderActions,
  isDeleting = false,
  isTogglingComplete = false,
}: SimpleNoteRowProps) {
  const displayTitle = note.title || note.content?.slice(0, 50) || "Untitled";
  const isCompleted = note.status === "COMPLETED" || note.completed === true;
  const noteList = note.noteLists?.[0]?.noteList;

  const handleToggleComplete = (checked: boolean | "indeterminate") => {
    if (typeof checked === "boolean" && onToggleComplete) {
      onToggleComplete(note.id, checked);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(note.id);
  };

  return (
    <motion.tr
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, height: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "group border-b transition-colors hover:bg-muted/50 last:border-b-0",
        isCompleted && "opacity-60",
        isDeleting && "opacity-40 pointer-events-none",
      )}
    >
      <TableCell className="w-10 py-2 px-3">
        <div onClick={(e) => e.stopPropagation()}>
          {isTogglingComplete ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Checkbox
              checked={isCompleted}
              onCheckedChange={handleToggleComplete}
              aria-label={
                isCompleted ? "Mark as incomplete" : "Mark as complete"
              }
              disabled={isTogglingComplete}
            />
          )}
        </div>
      </TableCell>

      <TableCell className="py-2 px-3">
        <div className="flex items-center gap-2 min-w-0">
          <Link
            to="/notes/$noteId"
            params={{ noteId: note.id }}
            className="flex-1 min-w-0"
          >
            <span
              className={cn(
                "text-sm font-normal line-clamp-1 transition-all",
                isCompleted
                  ? "line-through text-muted-foreground"
                  : "text-foreground/90 group-hover:text-foreground",
              )}
            >
              {displayTitle}
            </span>
          </Link>
          {noteList && (
            <Link
              to="/lists/$listId"
              params={{ listId: noteList.id }}
              onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0"
            >
              <Badge
                variant="outline"
                className="text-[10px] font-normal px-1.5 py-0 h-5 hover:bg-muted"
              >
                {noteList.name}
              </Badge>
            </Link>
          )}
        </div>
      </TableCell>

      <TableCell className="w-20 py-2 px-3">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {renderActions?.(note)}

          {onDelete && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete note</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TableCell>
    </motion.tr>
  );
}
